import { InMemoryCache, type ApolloCache, type NormalizedCacheObject, type PossibleTypesMap } from '@apollo/client';
import { parseDate } from '@internationalized/date';

import introspection from '../__generated__/possibleTypes.json';
import { GET_BANK_CONNECTIONS } from '../components/plaid/PlaidConnectionModal';
import { GET_ALL_FINANCIAL_ACCOUNTS, type FinancialAccount } from '../hooks/queries/useFinancialAccounts';

import type { GetBankConnectionsQuery } from '../components/plaid/PlaidConnectionModal/__generated__/PlaidConnectionModal.gql';
import type { GetAllFinancialAccountsQuery } from '../hooks/queries/__generated__/useFinancialAccounts.gql';

function readCalendarDate(value: unknown) {
  if (typeof value === 'string') {
    return parseDate(value);
  }
  return value ?? null;
}

/**
 * Creates a fresh InMemoryCache configured with the production type policies:
 * - possibleTypes from the introspection file
 * - CalendarDate read functions for Transaction, Bill, BillSubmission, RecurringTransaction
 * - Cursor-based pagination merge for getTransactionsForUser, getCallouts, getPaidObligationsForUser
 * - updateCallout mutation is explicitly not merged into the cache
 *
 * Each call returns a brand-new cache instance so that different consumers
 * (production Apollo client, Storybook pane provider) can operate on isolated state.
 */
export function createAppCache(): InMemoryCache {
  const possibleTypesMap = introspection.possibleTypes as unknown as PossibleTypesMap;
  return new InMemoryCache({
    possibleTypes: possibleTypesMap,
    typePolicies: {
      Transaction: {
        fields: {
          date: { read: readCalendarDate },
        },
      },
      Bill: {
        fields: {
          dueDate: { read: readCalendarDate },
          statementDate: { read: readCalendarDate },
          serviceStartDate: { read: readCalendarDate },
          serviceEndDate: { read: readCalendarDate },
          lateFeeDate: { read: readCalendarDate },
        },
      },
      BillSubmission: {
        fields: {
          dueDate: { read: readCalendarDate },
          statementDate: { read: readCalendarDate },
          serviceStartDate: { read: readCalendarDate },
          serviceEndDate: { read: readCalendarDate },
          lateFeeDate: { read: readCalendarDate },
        },
      },
      RecurringTransaction: {
        fields: {
          firstOccurrence: { read: readCalendarDate },
          latestOccurrence: { read: readCalendarDate },
          nextOccurrence: { read: readCalendarDate },
        },
      },
      Query: {
        fields: {
          getTransactionsForUser: {
            keyArgs: ['filters', 'sortKey'],
            merge(existing, incoming, { args }) {
              // If no cursor (first page), replace existing
              if (!args?.pagination?.cursor) {
                return incoming;
              }
              // Otherwise, merge edges
              const existingEdges = existing?.edges ?? [];
              const incomingEdges = incoming?.edges ?? [];
              return {
                ...incoming,
                edges: [...existingEdges, ...incomingEdges],
              };
            },
          },
          getCallouts: {
            keyArgs: ['maxPriority', 'filters'],
            merge(existing, incoming, { args }) {
              // If no cursor (first page), replace existing
              if (!args?.pagination?.cursor) {
                return incoming;
              }
              // Otherwise, merge edges
              const existingEdges = existing?.edges ?? [];
              const incomingEdges = incoming?.edges ?? [];
              return {
                ...incoming,
                edges: [...existingEdges, ...incomingEdges],
              };
            },
          },
          getPaidObligationsForUser: {
            keyArgs: false,
            merge(existing, incoming, { args }) {
              // If no cursor (first page), replace existing
              if (!args?.paidPagination?.cursor) {
                return incoming;
              }
              // Otherwise, merge edges
              const existingEdges = existing?.edges ?? [];
              const incomingEdges = incoming?.edges ?? [];
              return {
                ...incoming,
                edges: [...existingEdges, ...incomingEdges],
              };
            },
          },
        },
      },
      Mutation: {
        fields: {
          updateCallout: {
            merge: false,
          },
        },
      },
    },
  });
}

/**
 * Handle cache updates when a callout is dismissed.
 * Removes the callout from getCallouts queries and clears the transaction's callout field.
 */
function handleUpdateCallout(
  cache: ApolloCache<NormalizedCacheObject>,
  data: { updateCallout?: { id: string; dismissed: boolean; transaction?: { id: string } | null } }
) {
  if (!data?.updateCallout || data.updateCallout.dismissed !== true) {
    return;
  }

  const dismissedCalloutId = data.updateCallout.id;
  const transactionId = data.updateCallout.transaction?.id;

  // Remove dismissed callout from all getCallouts queries
  cache.modify({
    fields: {
      getCallouts(existingConnection, { readField }) {
        if (!existingConnection?.edges) {
          return existingConnection;
        }

        const updatedEdges = existingConnection.edges.filter((edge: { node: { id: string } }) => {
          const nodeId = readField('id', edge.node);
          return nodeId !== dismissedCalloutId;
        });

        return {
          ...existingConnection,
          edges: updatedEdges,
        };
      },
    },
  });

  // If callout was associated with a transaction, set its callout field to null
  if (transactionId) {
    cache.modify({
      id: cache.identify({ __typename: 'Transaction', id: transactionId }),
      fields: {
        callout: () => null,
      },
    });
  }
}

/**
 * Handle cache updates when financial accounts are created (via exchangePublicToken or createFinancialAccounts).
 * Merges new accounts into the existing cache and updates bank connections.
 */
function handleFinancialAccountsUpdate(
  cache: ApolloCache<NormalizedCacheObject>,
  data: {
    exchangePublicToken?: FinancialAccount[];
    createFinancialAccounts?: FinancialAccount[];
  }
) {
  const newAccounts = data?.exchangePublicToken ?? data?.createFinancialAccounts;
  if (!newAccounts?.length) {
    return;
  }

  // Update financial accounts cache
  const existingAccountsData = cache.readQuery<GetAllFinancialAccountsQuery>({
    query: GET_ALL_FINANCIAL_ACCOUNTS,
  });

  const combinedAccounts = [...(existingAccountsData?.getFinancialAccountsForUser ?? []), ...newAccounts];

  // Deduplicate by id, keeping the last occurrence (newer data)
  const uniqueAccounts = Array.from(new Map(combinedAccounts.map((account) => [account.id, account])).values());

  cache.writeQuery<GetAllFinancialAccountsQuery>({
    query: GET_ALL_FINANCIAL_ACCOUNTS,
    data: {
      getFinancialAccountsForUser: uniqueAccounts,
    },
  });

  // Update bank connections cache
  const existingBankConnectionsData = cache.readQuery<GetBankConnectionsQuery>({
    query: GET_BANK_CONNECTIONS,
  });

  // Extract unique bank connections from new accounts
  const newBankConnections = Array.from(
    new Map(
      newAccounts.map((account) => [
        account.bankConnection.id,
        {
          __typename: 'BankConnection' as const,
          id: account.bankConnection.id,
          bank: account.bank,
        },
      ])
    ).values()
  );

  const combinedBankConnections = [
    ...(existingBankConnectionsData?.getBankConnectionsForUser ?? []),
    ...newBankConnections,
  ];

  // Deduplicate by id, keeping the last occurrence (newer data)
  const uniqueBankConnections = Array.from(
    new Map(combinedBankConnections.map((connection) => [connection.id, connection])).values()
  );

  cache.writeQuery<GetBankConnectionsQuery>({
    query: GET_BANK_CONNECTIONS,
    data: {
      getBankConnectionsForUser: uniqueBankConnections,
    },
  });
}

/**
 * Handle cache updates when bank connection callouts are dismissed.
 * Removes the dismissed callouts from getCallouts queries.
 */
function handleDismissBankConnectionCallout(
  cache: ApolloCache<NormalizedCacheObject>,
  data: { dismissBankConnectionCallout?: string[] }
) {
  const dismissedIds = data?.dismissBankConnectionCallout;
  if (!dismissedIds?.length) {
    return;
  }

  // Remove dismissed callouts from all getCallouts queries
  cache.modify({
    fields: {
      getCallouts(existingConnection, { readField }) {
        if (!existingConnection?.edges) {
          return existingConnection;
        }

        const updatedEdges = existingConnection.edges.filter((edge: { node: { id: string } }) => {
          const nodeId = readField('id', edge.node);
          return !dismissedIds.includes(nodeId as string);
        });

        return {
          ...existingConnection,
          edges: updatedEdges,
        };
      },
    },
  });
}

interface IMutationResultData {
  updateCallout?: { id: string; dismissed: boolean; transaction?: { id: string } | null };
  exchangePublicToken?: FinancialAccount[];
  createFinancialAccounts?: FinancialAccount[];
  dismissBankConnectionCallout?: string[];
}

/**
 * Builds the centralized mutation update handler that composes cache updates
 * for callout dismissal, financial account creation, and bank connection callout dismissal.
 *
 * Wire this into ApolloClient's defaultOptions.mutate.update so every mutation
 * automatically flows through all three handlers.
 */
export function createMutationUpdateHandler() {
  return (cache: ApolloCache<NormalizedCacheObject>, { data }: { data?: unknown }) => {
    const safeData = (data ?? {}) as IMutationResultData;
    handleUpdateCallout(cache, safeData);
    handleFinancialAccountsUpdate(cache, safeData);
    handleDismissBankConnectionCallout(cache, safeData);
  };
}
